import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from "react-router-dom";

import Login from "./routes/Login/Login";
import Logout from "./routes/Logout/Logout";
import VerifyEmail from "./routes/VerifyEmail/VerifyEmail";
import ResetPassword from './routes/ResetPassword/ResetPassword';
import ChooseNewPassword from './routes/ChooseNewPassword/ChooseNewPassword';

import Account from "./routes/Account/Account";
import BalancesPayouts from "./routes/BalancesPayouts/BalancesPayouts";
import DogProfile from "./routes/DogProfile/DogProfile";
import Post from "./routes/Post/Post";
import Admin from "./routes/Admin/Admin";

import Register from "./routes/Register/Register";
import DogOwner from "./routes/Register/DogOwner/DogOwner";
import KennelClubRegister from "./routes/Register/KennelClub/KennelClub";

import Events from "./routes/Events/Events";
import Courses from "./routes/Courses/Courses";
import Course from "./routes/Course/Course";
import CourseRegister from './routes/CourseRegister/CourseRegister';
import CourseManage from "./routes/CourseManage/CourseManage";
import Event from "./routes/Event/Event";
import EventSummary from "./routes/EventSummary";
import EventRegister from "./routes/EventRegister/EventRegister";
import EventManage from "./routes/EventManage/EventManage";
import TrustedSecretary from "./routes/TrustedSecretary/TrustedSecretary";
import PrivacyPolicy from "./routes/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./routes/TermsOfService/TermsOfService";
import CommunityGuidelines from "./routes/CommunityGuidelines/CommunityGuidelines";
import KennelClub from "./routes/KennelClub/KennelClub";
import KennelClubManage from "./routes/KennelClubManage/KennelClubManage";
import KennelClubs from "./routes/KennelClubs/KennelClubs";
import EmbedCalendar from "./routes/EmbedCalendar/EmbedCalendar";
import StoreCheckout from "./routes/StoreCart/StoreCheckout";
import Discover from "./routes/Discover/Discover";

import NotFound from "./routes/NotFound/NotFound";
import Header from "./components/Header/Header";
import Messages from "./components/Messages/Messages";
import ShareToFeedModalHost from "./routes/Discover/Feed/ShareToFeed/ShareToFeedModalHost";
import { MessagesProvider } from "./context/MessagesContext";

import EventPayment from "./routes/EventLotteryPayment/EventPayment";
import PaymentRequest from "./routes/EventPaymentRequest/PaymentRequest";

import Documents from "./routes/Documents";

import { TariffPlan } from "./routes/TariffPlans";
import { SubscriptionCheckoutReturn } from "./routes/TariffPlans/SubscriptionCheckoutReturn";
import StripeConnectReturn from "./routes/StripeConnectReturn/StripeConnectReturn";

import { ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";

import './App.css';
import ApproveDogProposedChanges from "./routes/ApproveDogProposedChanges/ApproveDogProposedChanges";

function App() {
  const location = useLocation();
  const [locationStyle, setLocationStyle] = useState<string>();

  ModuleRegistry.registerModules([AllEnterpriseModule]);
  LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-073985}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Eager_Dog}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Eager_Dog}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Eager_Dog}_need_to_be_licensed___{Eager_Dog}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{18_December_2025}____[v3]_[01]_MTc2NjAxNjAwMDAwMA==c21f46ae2d9923cd681b6f93796380ae");

  const hiddenHeaderLocations:string[] = [
    "/login",
    "/register",
    "/register/kennel-club",
    "/register/dog-owner",
    "/verify-email",
    "/send-code",
    "/reset-password"
  ];

  // Check if current path should hide the main header  
  const shouldHideHeader = hiddenHeaderLocations.includes(location.pathname) || location.pathname.startsWith("/embed");

  useEffect(() => {
    document.body.style.overflow = 'unset';
    let _locationStyle = location.pathname.split("/");

    if (_locationStyle.length > 1 && _locationStyle[1].length > 0) {
      setLocationStyle(_locationStyle[1].toLowerCase() + "Page");
    } else {
      setLocationStyle("homePage");
    }

  }, [location.pathname]);

  return (
    <MessagesProvider>
    <div className={shouldHideHeader ? "Eagerdog noHeader " + locationStyle : "Eagerdog " + locationStyle}>
      {!shouldHideHeader && <Header />}
      <Messages />
      <ShareToFeedModalHost />
      <Routes>
        <Route path="/" element={<Discover />} />
        <Route path="/events" element={<Events />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/c/:cid" element={<Course />} />
        <Route path="/c/:cid/register" element={<CourseRegister />} />
        <Route path="/c/:cid/manage" element={<CourseManage />} />
        <Route path="/kennel-clubs" element={<KennelClubs />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/register/dog-owner" element={<DogOwner />} />
        <Route path="/register/kennel-club" element={<KennelClubRegister />} />
        <Route path="/send-code" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ChooseNewPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/e/:eid" element={<Event />} />
        <Route path="/e/:eid/summary" element={<EventSummary />} />
        <Route path="/e/:eid/register" element={<EventRegister />} />
        <Route path="/e/:eid/manage" element={<EventManage />} />
        <Route path="/e/:eid/lottery-payment" element={<EventPayment />} />
        <Route path="/e/:eid/waitlist-payment" element={<EventPayment />} />
        <Route path="/e/:eid/payment-request" element={<PaymentRequest />} />
        <Route path="/e/:eid/approve-dog-changes/:dogId" element={<ApproveDogProposedChanges />} />
        <Route path="/dogs/:dogId" element={<DogProfile />} />
        <Route path="/p/:id" element={<Post />} />
        <Route path="/account" element={<Account />} />
        <Route path="/trusted-secretary" element={<TrustedSecretary />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/balances-payouts" element={<BalancesPayouts />} />
        <Route path="/embed/club/:clubId/calendar" element={<EmbedCalendar />} />
        <Route path="/k/:clubId/:tab?" element={<KennelClub />} />
        <Route path="/k/:clubId/store/checkout" element={<StoreCheckout />} />
        <Route path="/k/:clubId/manage/:tab?" element={<KennelClubManage />} />
        <Route path="/documents/:id" element={<Documents />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        <Route path="/premium" element={<TariffPlan />} />
        <Route path="/subscription-feedback" element={<SubscriptionCheckoutReturn />} />
        <Route path="/stripe/connect/return" element={<StripeConnectReturn />} />
        <Route path='*' element={<NotFound />}/>
      </Routes>
    </div>
    </MessagesProvider>
  );
}

export default App;